import type { FC } from "react";
import { useCallback, useEffect, useRef } from "react";

import classNames from "classnames";

import styles from "./BackgroundVideo.module.scss";

const HAVE_CURRENT_DATA_READY_STATE = 2;

export type BackgroundVideoProps = {
  desktopSrc: string;
  desktopType?: string;
  tabletSrc?: string;
  tabletType?: string;
  mobileSrc?: string;
  mobileType?: string;
  poster?: string;
  loop?: boolean;
  muted?: boolean;
  autoPlay?: boolean;
  playsInline?: boolean;
  preload?: "auto" | "metadata" | "none";
  removeRoundedCorners?: boolean;
  videoClassName?: string;
  className?: string;
  ariaLabel?: string;
  onReady?: () => void;
};

export const BackgroundVideo: FC<BackgroundVideoProps> = ({
  desktopSrc,
  desktopType,
  tabletSrc,
  tabletType,
  mobileSrc,
  mobileType,
  poster,
  loop = true,
  muted = true,
  autoPlay = true,
  playsInline = true,
  preload = "auto",
  removeRoundedCorners = false,
  videoClassName,
  className,
  ariaLabel,
  onReady,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasReportedReadyRef = useRef(false);

  useEffect(() => {
    // Reset the ready state when sources change
    // to ensure onReady is called for the new video sources.
    hasReportedReadyRef.current = false;
  }, [desktopSrc, tabletSrc, mobileSrc]);

  const handleReady = useCallback(() => {
    if (hasReportedReadyRef.current) {
      return;
    }

    hasReportedReadyRef.current = true;
    onReady?.();
  }, [onReady]);

  useEffect(() => {
    const video = videoRef.current;

    if (!video || video.readyState < HAVE_CURRENT_DATA_READY_STATE) {
      return;
    }

    handleReady();
  }, [desktopSrc, handleReady, mobileSrc, tabletSrc]);

  return (
    <div
      className={classNames(styles.wrapper, className, {
        [styles.noRoundedCorners]: removeRoundedCorners,
      })}
      data-testid="video-element"
    >
      {/* Decorative background video is muted and aria-hidden by default; captions are not applicable. */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={videoRef}
        className={classNames(styles.video, videoClassName)}
        autoPlay={autoPlay}
        loop={loop}
        muted={muted}
        playsInline={playsInline}
        preload={preload}
        poster={poster}
        aria-label={ariaLabel}
        aria-hidden={ariaLabel ? undefined : true}
        onCanPlay={handleReady}
        onPlaying={handleReady}
      >
        {mobileSrc && <source src={mobileSrc} media="(max-width: 479px)" type={mobileType} />}
        {tabletSrc && <source src={tabletSrc} media="(max-width: 959px)" type={tabletType} />}
        <source src={desktopSrc} type={desktopType} />
      </video>
    </div>
  );
};
